import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, fmtMoney, extractErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FileText, Printer, CheckCircle2, AlertTriangle, Lock } from "lucide-react";

// Server-authoritative document printing charge: per-document page counts, total, rate and charge.
// Client confirms before paying; Admin enters manual counts for non-PDF / unreadable files.
export function PrintingCharge({ orderId, role = "client", onStatus, onChanged }) {
  const isAdmin = role === "admin";
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);
  const [checked, setChecked] = useState(false);
  const [counts, setCounts] = useState({});

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/orders/${orderId}/printing`);
      setP(data);
      if (data?.confirmation?.confirmed) setChecked(true);
      onStatus && onStatus(!!data?.payment_blocked);
    } catch { /* no printing */ }
  }, [orderId, onStatus]);

  useEffect(() => { load(); }, [load]);

  if (!p || !p.has_documents) return null;
  const cur = "USD";
  const locked = !!p.snapshot_locked;

  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/orders/${orderId}/printing/confirm`, { confirmed: true, fingerprint: p.fingerprint });
      setP(data); setChecked(true);
      onStatus && onStatus(!!data?.payment_blocked);
      onChanged && onChanged();
      toast.success("Printing charge confirmed");
    } catch (e) { toast.error(extractErrorMessage(e, "Could not confirm")); load(); }
    finally { setBusy(false); }
  };

  const saveCount = async (fileId) => {
    const pages = parseInt(counts[fileId], 10);
    if (isNaN(pages) || pages < 0) { toast.error("Enter a valid page count"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/orders/${orderId}/printing/manual-count`, { file_id: fileId, pages });
      setP(data);
      onStatus && onStatus(!!data?.payment_blocked);
      onChanged && onChanged();
      toast.success("Page count saved");
    } catch (e) { toast.error(extractErrorMessage(e, "Could not save count")); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="printing-charge-panel" className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="mb-2 flex items-center gap-2">
        <Printer className="h-4 w-4 text-slate-600" />
        <h4 className="font-display text-sm font-bold text-slate-900">Document printing</h4>
        {locked && <span data-testid="printing-locked-badge" className="ml-auto inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-500"><Lock className="h-3 w-3" /> Locked</span>}
      </div>

      <div className="divide-y divide-slate-100 border-y border-slate-100">
        {(p.documents || []).map((d) => (
          <div key={d.file_id} data-testid={`printing-doc-${d.file_id}`} className="flex items-center justify-between gap-3 py-1.5 text-sm">
            <span className="flex min-w-0 items-center gap-2 text-slate-600">
              <FileText className="h-4 w-4 shrink-0 text-emerald-600" />
              <span className="truncate">{d.name}</span>
            </span>
            <span className="shrink-0 text-right">
              {d.resolved ? (
                <span className="font-medium text-slate-800" data-testid={`printing-pages-${d.file_id}`}>
                  {d.pages} pg{d.source === "admin_manual" ? " (admin)" : d.source === "pdf_auto" ? " (auto)" : ""}
                </span>
              ) : isAdmin ? (
                <span className="inline-flex items-center gap-1">
                  <Input data-testid={`printing-count-input-${d.file_id}`} type="number" min="0" className="h-7 w-20 text-right"
                    value={counts[d.file_id] ?? ""} onChange={(e) => setCounts({ ...counts, [d.file_id]: e.target.value })} />
                  <Button data-testid={`printing-count-save-${d.file_id}`} size="sm" className="h-7 bg-slate-900 text-white hover:bg-slate-800" disabled={busy} onClick={() => saveCount(d.file_id)}>Set</Button>
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-amber-600" data-testid={`printing-pending-${d.file_id}`}><AlertTriangle className="h-3.5 w-3.5" /> Pending ARPS count</span>
              )}
            </span>
          </div>
        ))}
      </div>

      <div className="mt-2 space-y-1 text-sm">
        <div className="flex justify-between"><span className="text-slate-500">Total pages</span><span data-testid="printing-total-pages" className="font-medium text-slate-800">{p.total_pages}{p.resolved ? "" : "+"}</span></div>
        <div className="flex justify-between"><span className="text-slate-500">Rate{p.rate_source === "client_terms" ? " (your rate)" : ""}</span><span className="font-medium text-slate-800">{fmtMoney(cur, p.rate)}/pg</span></div>
        <div className="flex justify-between"><span className="font-semibold text-slate-900">Printing charge</span><span data-testid="printing-charge" className="font-bold text-emerald-700">{fmtMoney(cur, p.charge)}</span></div>
      </div>

      {!isAdmin && p.confirmation_required && !locked && (
        p.resolved ? (
          <div className="mt-3">
            {p.confirmation?.confirmed && p.confirmation?.fingerprint === p.fingerprint ? (
              <div data-testid="printing-confirmed" className="inline-flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800"><CheckCircle2 className="h-4 w-4" /> Printing charge confirmed</div>
            ) : (
              <div className="space-y-2 rounded-lg border border-slate-200 p-3">
                <label data-testid="printing-confirm-checkbox" className="flex cursor-pointer items-start gap-2 text-sm text-slate-700">
                  <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} className="mt-0.5 h-4 w-4" />
                  <span>I confirm the document page count and printing charge are correct.</span>
                </label>
                <Button data-testid="printing-confirm-button" disabled={!checked || busy} onClick={confirm} className="w-full bg-slate-900 text-white hover:bg-slate-800">
                  {busy ? "Confirming…" : "Confirm printing charge"}
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div data-testid="printing-pending-note" className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-700">
            Some documents need an ARPS page count before payment. We'll notify you once the total is ready to confirm.
          </div>
        )
      )}
    </div>
  );
}
